import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";

export const B2BAblauf = () => {
  const [refSteps, isStepsVisible] = useScrollReveal({ once: true });
  const [refLogistik, isLogistikVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Apple Style */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Ablauf & Logistik
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Einfacher und strukturierter Prozess für Ihren Firmen-Laptop-Ankauf
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt starten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* 6 Steps */}
      <section ref={refSteps} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isStepsVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Der Ankaufsprozess in 6 Schritten
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isStepsVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="max-w-5xl mx-auto space-y-8"
          >
            {[
              { step: 1, icon: Icons.FileText, title: "Anfrage stellen", desc: "Füllen Sie unser Online-Formular aus oder kontaktieren Sie uns telefonisch. Geben Sie Details zu Anzahl, Hersteller und Modellen an.", time: "5 Minuten" },
              { step: 2, icon: Icons.Search, title: "Bestandsprüfung", desc: "Unser Team prüft Ihre Geräteliste und bewertet die Geräte anhand der angegebenen Informationen.", time: "24-48 Stunden" },
              { step: 3, icon: Icons.FileCheck, title: "Angebot erhalten", desc: "Sie erhalten ein detailliertes, unverbindliches Angebot per E-Mail mit transparenter Preisaufstellung.", time: "48 Stunden" },
              { step: 4, icon: Icons.Truck, title: "Abholung vereinbaren", desc: "Nach Ihrer Zusage koordinieren wir einen Abholtermin. Unsere Logistikpartner holen die Geräte kostenlos ab.", time: "3-7 Tage" },
              { step: 5, icon: Icons.ClipboardCheck, title: "Detailprüfung & Datenlöschung", desc: "Finale Prüfung der Geräte und DSGVO-konforme Datenlöschung mit Zertifikat.", time: "2-5 Tage" },
              { step: 6, icon: Icons.Euro, title: "Auszahlung", desc: "Nach finaler Prüfung erfolgt die Überweisung des vereinbarten Betrags auf Ihr Konto.", time: "1-3 Tage" }
            ].map((item) => (
              <motion.div key={item.step} variants={slideUp}>
                <PremiumCard className="p-6">
                  <div className="flex gap-6">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="flex-shrink-0"
                    >
                      <div className="w-16 h-16 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] text-white rounded-full flex items-center justify-center font-bold text-xl">
                        {item.step}
                      </div>
                    </motion.div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <item.icon className="text-[#1f7db6]" size={24} />
                        <h3 className="font-bold text-xl text-[#143e6e]">{item.title}</h3>
                      </div>
                      <p className="text-gray-700 mb-2">{item.desc}</p>
                      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                        <Icons.Clock size={16} />
                        {item.time}
                      </span>
                    </div>
                  </div>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Logistik */}
      <section ref={refLogistik} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isLogistikVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Logistik & Abholung
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isLogistikVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              { icon: Icons.MapPin, title: "Deutschlandweit", desc: "Abholung in ganz Deutschland" },
              { icon: Icons.Calendar, title: "Flexibel", desc: "Wunschtermin innerhalb von 3-7 Tagen" },
              { icon: Icons.Package, title: "Kostenlos", desc: "Keine Versandkosten für Sie" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Bereit für einen reibungslosen Ablauf?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Starten Sie jetzt Ihre Anfrage
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Anfrage starten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
